import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit'
import { 
  HRMessage, 
  HRChatSession, 
  ChatPreferences,
  MessageSource,
  Feedback 
} from '@/types/chat.types'
import { HRService } from '@/services/hrService'

interface HRChatState {
  sessions: Record<string, HRChatSession>
  sessionsList: HRChatSession[]
  activeSessionId: string | null
  loadingSessions: boolean
  sessionsError: string | null
  preferences: ChatPreferences
  streaming: {
    enabled: boolean
    currentStreamingMessage: string | null
  }
  uiState: {
    showSources: string | null
  }
}

const initialState: HRChatState = {
  sessions: {},
  sessionsList: [],
  activeSessionId: null,
  loadingSessions: false,
  sessionsError: null,
  preferences: {
    translateToSpanish: false,
    language: 'en'
  },
  streaming: {
    enabled: true,
    currentStreamingMessage: null
  },
  uiState: {
    showSources: null
  }
}

// Async thunks
export const fetchChatSessions = createAsyncThunk<HRChatSession[], number>(
  'chat/fetchSessions',
  async (limit: number = 10) => {
    const response = await HRService.getChatSessions(limit)
    return response
  }
)

export const loadChatSession = createAsyncThunk<HRChatSession, string>(
  'chat/loadSession',
  async (sessionId: string) => {
    const response = await HRService.getChatSession(sessionId)
    return response
  }
)

export const sendChatMessage = createAsyncThunk<
  HRMessage,
  { 
    content: string
    sessionId?: string | null
    preferences: ChatPreferences 
  }
>(
  'chat/sendMessage',
  async ({ 
    content, 
    sessionId, 
    preferences 
  }) => {
    const response = await HRService.sendChatMessage({
      content,
      sessionId: sessionId || undefined,
      agentType: 'HR',
      confidenceThreshold: 0.7,
      translateToSpanish: preferences.translateToSpanish || preferences.language !== 'en',
      language: preferences.language,
      sessionTitle: !sessionId ? `HR Chat ${new Date().toLocaleDateString()}` : undefined
    })
    return response
  }
)

export const submitMessageFeedback = createAsyncThunk(
  'chat/submitFeedback',
  async ({ 
    messageId, 
    rating, 
    comment 
  }: { 
    messageId: string
    rating: number
    comment?: string 
  }) => {
    await HRService.submitFeedback(messageId, { rating, comment })
    return { messageId, rating, comment }
  }
)

export const updateChatSession = createAsyncThunk<
  HRChatSession,
  { sessionId: string; updates: { title?: string; isActive?: boolean } }
>(
  'chat/updateSession',
  async ({ sessionId, updates }) => {
    const response = await HRService.updateChatSession(sessionId, updates)
    return response
  }
)

export const deleteChatSession = createAsyncThunk<
  { sessionId: string },
  string
>(
  'chat/deleteSession',
  async (sessionId: string) => {
    await HRService.deleteChatSession(sessionId)
    return { sessionId }
  }
)

const hrChatSlice = createSlice({
  name: 'hrChat',
  initialState,
  reducers: {
    setActiveSession: (state, action: PayloadAction<string | null>) => {
      state.activeSessionId = action.payload
    },
    
    // Transfer messages from temp session to real session
    transferTempSession: (state, action: PayloadAction<{ 
      tempSessionId: string
      realSessionId: string 
    }>) => {
      const { tempSessionId, realSessionId } = action.payload
      
      // Move messages from temp session to real session
      if (state.sessions[tempSessionId] && state.sessions[tempSessionId].messages.length > 0) {
        // Create real session if it doesn't exist
        if (!state.sessions[realSessionId]) {
          state.sessions[realSessionId] = {
            id: realSessionId,
            agentType: 'HR',
            title: state.sessions[tempSessionId].title,
            messages: [],
            isLoading: false,
            error: null,
            isActive: true,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          }
        }
        
        // Transfer messages
        state.sessions[realSessionId].messages = [...state.sessions[tempSessionId].messages]
        
        // Clean up temp session
        delete state.sessions[tempSessionId]
      }
      
      // Switch to real session
      state.activeSessionId = realSessionId
    },
    addOptimisticMessage: (state, action: PayloadAction<{ sessionId: string; message: HRMessage }>) => {
      const { sessionId, message } = action.payload
      
      // Create session if it doesn't exist (for temporary sessions)
      if (!state.sessions[sessionId]) {
        state.sessions[sessionId] = {
          id: sessionId,
          agentType: 'HR',
          title: `HR Chat ${new Date().toLocaleDateString()}`,
          messages: [],
          isLoading: false,
          error: null,
          isActive: true,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        }
      }
      
      state.sessions[sessionId].messages.push(message)
    },
    updateStreamingMessageId: (state, action: PayloadAction<{ sessionId: string; newMessageId: string }>) => {
      const { sessionId, newMessageId } = action.payload
      const session = state.sessions[sessionId]
      
      if (session) {
        // Find the current streaming message (should be the last ASSISTANT message with isStreaming: true)
        const streamingMessageIndex = session.messages.findIndex(msg => 
          msg.role === 'ASSISTANT' && msg.isStreaming
        )
        
        if (streamingMessageIndex !== -1) {
          // Update the message ID to match the backend streaming message ID
          session.messages[streamingMessageIndex].id = newMessageId
          console.log('🔄 Updated streaming message ID to:', newMessageId)
        }
      }
    },
    removeOptimisticMessage: (state, action: PayloadAction<{ sessionId: string; messageId: string }>) => {
      const { sessionId, messageId } = action.payload
      if (state.sessions[sessionId]) {
        state.sessions[sessionId].messages = state.sessions[sessionId].messages.filter(
          msg => msg.id !== messageId
        )
      }
    },
    updateMessage: (state, action: PayloadAction<{ sessionId: string; messageId: string; message: Partial<HRMessage> }>) => {
      const { sessionId, messageId, message } = action.payload
      if (state.sessions[sessionId]) {
        const msgIndex = state.sessions[sessionId].messages.findIndex(msg => msg.id === messageId)
        if (msgIndex !== -1) {
          state.sessions[sessionId].messages[msgIndex] = {
            ...state.sessions[sessionId].messages[msgIndex],
            ...message
          }
        }
      }
    },
    setPreferences: (state, action: PayloadAction<Partial<ChatPreferences>>) => {
      state.preferences = { ...state.preferences, ...action.payload }
    },
    toggleSourcesDisplay: (state, action: PayloadAction<string | null>) => {
      state.uiState.showSources = action.payload
    },
    clearError: (state, action: PayloadAction<string>) => {
      const sessionId = action.payload
      if (state.sessions[sessionId]) {
        state.sessions[sessionId].error = null
      }
    },
    clearSessionsError: (state) => {
      state.sessionsError = null
    },
    startNewChat: (state) => {
      state.activeSessionId = null
      state.uiState.showSources = null
    },
    // Streaming-specific actions
    enableStreaming: (state, action: PayloadAction<boolean>) => {
      state.streaming.enabled = action.payload
    },
    startStreamingMessage: (state, action: PayloadAction<{ sessionId: string; messageId: string }>) => {
      const { sessionId, messageId } = action.payload
      state.streaming.currentStreamingMessage = messageId
      
      // Create a placeholder message for streaming
      if (state.sessions[sessionId]) {
        const placeholderMessage: HRMessage = {
          id: messageId,
          role: 'ASSISTANT',
          content: '',
          isStreaming: true,
          createdAt: new Date().toISOString()
        }
        state.sessions[sessionId].messages.push(placeholderMessage)
      }
    },
    updateStreamingMessage: (state, action: PayloadAction<{ 
      sessionId: string
      messageId: string
      content: string
      isComplete?: boolean
      citations?: MessageSource[]
    }>) => {
      const { sessionId, messageId, content, isComplete, citations } = action.payload
      
      if (state.sessions[sessionId]) {
        const msgIndex = state.sessions[sessionId].messages.findIndex(msg => msg.id === messageId)
        if (msgIndex !== -1) {
          const message = state.sessions[sessionId].messages[msgIndex]
          message.content = content
          if (isComplete) {
            message.isStreaming = false
            if (citations) {
              message.sources = citations
            }
          }
        }
      }
      
      if (isComplete && state.streaming.currentStreamingMessage === messageId) {
        state.streaming.currentStreamingMessage = null
      }
    },
    streamingError: (state, action: PayloadAction<{ 
      sessionId: string
      messageId: string
      error: string 
    }>) => {
      const { sessionId, messageId, error } = action.payload
      
      if (state.sessions[sessionId]) {
        const msgIndex = state.sessions[sessionId].messages.findIndex(msg => msg.id === messageId)
        if (msgIndex !== -1) {
          const message = state.sessions[sessionId].messages[msgIndex]
          message.isStreaming = false
          message.content = 'Sorry, I encountered an error while generating a response. Please try again.'
          message.error = error
        }
      }
      
      if (state.streaming.currentStreamingMessage === messageId) {
        state.streaming.currentStreamingMessage = null
      }
    },
    cancelStreaming: (state, action: PayloadAction<{ messageId: string }>) => {
      const { messageId } = action.payload
      if (state.streaming.currentStreamingMessage === messageId) {
        state.streaming.currentStreamingMessage = null
      }
    },
    updateSessionTitle: (state, action: PayloadAction<{ sessionId: string; title: string; updatedAt: string }>) => {
      const { sessionId, title, updatedAt } = action.payload
      
      // Update in the sessions record
      if (state.sessions[sessionId]) {
        state.sessions[sessionId].title = title
        state.sessions[sessionId].updatedAt = updatedAt
      }
      
      // Update in the sessions list
      const sessionIndex = state.sessionsList.findIndex(session => session.id === sessionId)
      if (sessionIndex !== -1) {
        state.sessionsList[sessionIndex].title = title
        state.sessionsList[sessionIndex].updatedAt = updatedAt
      }
    },
    removeSession: (state, action: PayloadAction<string>) => {
      const sessionId = action.payload
      
      // Remove from sessions record
      delete state.sessions[sessionId]
      
      // Remove from sessions list
      state.sessionsList = state.sessionsList.filter(session => session.id !== sessionId)
      
      // If this was the active session, clear it
      if (state.activeSessionId === sessionId) {
        state.activeSessionId = null
      }
    }
  },
  extraReducers: (builder) => {
    // Fetch sessions
    builder
      .addCase(fetchChatSessions.pending, (state) => {
        state.loadingSessions = true
        state.sessionsError = null
      })
      .addCase(fetchChatSessions.fulfilled, (state, action) => {
        state.loadingSessions = false
        state.sessionsList = action.payload
        
        // Also populate the sessions record for easy access
        action.payload.forEach(session => {
          state.sessions[session.id] = session
        })
      })
      .addCase(fetchChatSessions.rejected, (state, action) => {
        state.loadingSessions = false
        state.sessionsError = action.error.message || 'Failed to load sessions'
      })
    
    // Load session
    builder
      .addCase(loadChatSession.pending, (state, action) => {
        const sessionId = action.meta.arg
        if (!state.sessions[sessionId]) {
          state.sessions[sessionId] = {
            id: sessionId,
            agentType: 'HR',
            messages: [],
            isLoading: true,
            error: null,
            isActive: true,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          }
        } else {
          state.sessions[sessionId].isLoading = true
        }
      })
      .addCase(loadChatSession.fulfilled, (state, action) => {
        const session = action.payload
        state.sessions[session.id] = {
          ...session,
          isLoading: false,
          error: null
        }
        state.activeSessionId = session.id
      })
      .addCase(loadChatSession.rejected, (state, action) => {
        const sessionId = action.meta.arg
        if (state.sessions[sessionId]) {
          state.sessions[sessionId].isLoading = false
          state.sessions[sessionId].error = action.error.message || 'Failed to load session'
        }
      })
    
    // Send message
    builder
      .addCase(sendChatMessage.pending, (state, action) => {
        // Don't set session loading state for message sending
        // This is now handled by local state in the hook
        const sessionId = action.meta.arg.sessionId || state.activeSessionId
        if (sessionId && state.sessions[sessionId]) {
          state.sessions[sessionId].error = null
        }
      })
      .addCase(sendChatMessage.fulfilled, (state, action) => {
        const assistantMessage = action.payload
        const sessionId = assistantMessage.sessionId
        const { content } = action.meta.arg // Get the original user message content
        
        // Handle new session creation
        if (assistantMessage.isNewSession && sessionId) {
          // Clean up any temporary sessions
          const tempSessionIds = Object.keys(state.sessions).filter(id => id.startsWith('temp-session-'))
          tempSessionIds.forEach(tempId => {
            delete state.sessions[tempId]
          })
          
          // Create or update the real session
          if (!state.sessions[sessionId]) {
            state.sessions[sessionId] = {
              id: sessionId,
              agentType: 'HR',
              title: `HR Chat ${new Date().toLocaleDateString()}`,
              messages: [],
              isLoading: false,
              error: null,
              isActive: true,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString()
            }
          }
          state.activeSessionId = sessionId
        }
        
        if (sessionId && state.sessions[sessionId]) {
          // Create user message
          const userMessage: HRMessage = {
            id: `user-${assistantMessage.id}`,
            role: 'USER',
            content,
            createdAt: new Date().toISOString()
          }
          
          // Add both user message and assistant response
          state.sessions[sessionId].messages.push(userMessage)
          state.sessions[sessionId].messages.push(assistantMessage)
          // Don't modify isLoading here - it's for session loading only
        }
      })
      .addCase(sendChatMessage.rejected, (state, action) => {
        const sessionId = action.meta.arg.sessionId || state.activeSessionId
        if (sessionId && state.sessions[sessionId]) {
          // Don't modify isLoading here - it's for session loading only
          state.sessions[sessionId].error = action.error.message || 'Failed to send message'
        }
      })
    
    // Submit feedback
    builder
      .addCase(submitMessageFeedback.fulfilled, (state, action) => {
        const { messageId, rating, comment } = action.payload
        // Find the message across all sessions and update it
        Object.values(state.sessions).forEach(session => {
          const message = session.messages.find(msg => msg.id === messageId)
          if (message) {
            message.feedback = [{
              id: `feedback-${Date.now()}`,
              rating,
              comment,
              createdAt: new Date().toISOString()
            }]
          }
        })
      })
    
    // Update session
    builder
      .addCase(updateChatSession.fulfilled, (state, action) => {
        const updatedSession = action.payload
        
        // Update in the sessions record
        if (state.sessions[updatedSession.id]) {
          state.sessions[updatedSession.id] = {
            ...state.sessions[updatedSession.id],
            ...updatedSession
          }
        }
        
        // Update in the sessions list
        const sessionIndex = state.sessionsList.findIndex(session => session.id === updatedSession.id)
        if (sessionIndex !== -1) {
          state.sessionsList[sessionIndex] = {
            ...state.sessionsList[sessionIndex],
            ...updatedSession
          }
        }
      })
      .addCase(updateChatSession.rejected, (state, action) => {
        // Handle error if needed
        console.error('Failed to update session:', action.error.message)
      })
    
    // Delete session
    builder
      .addCase(deleteChatSession.fulfilled, (state, action) => {
        const { sessionId } = action.payload
        
        // Remove from sessions record
        delete state.sessions[sessionId]
        
        // Remove from sessions list
        state.sessionsList = state.sessionsList.filter(session => session.id !== sessionId)
        
        // If this was the active session, clear it
        if (state.activeSessionId === sessionId) {
          state.activeSessionId = null
        }
      })
      .addCase(deleteChatSession.rejected, (state, action) => {
        // Handle error if needed
        console.error('Failed to delete session:', action.error.message)
      })
  }
})

export const {
  setActiveSession,
  transferTempSession,
  addOptimisticMessage,
  updateStreamingMessageId,
  removeOptimisticMessage,
  updateMessage,
  setPreferences,
  toggleSourcesDisplay,
  clearError,
  clearSessionsError,
  startNewChat,
  enableStreaming,
  startStreamingMessage,
  updateStreamingMessage,
  streamingError,
  cancelStreaming,
  updateSessionTitle,
  removeSession
} = hrChatSlice.actions

export default hrChatSlice.reducer